import { Accessibility } from '@mui/icons-material';
import dayjs from 'dayjs';

import { selectIsAuthenticated } from '@/domain/auth/auth.selectors';
import { CalculateVenueCo2 } from '@/domain/impact-calculate-venue/entities/calculate-venue-co2.entity';
import { CalculateVenueEss } from '@/domain/impact-calculate-venue/entities/calculate-venue-ess.entity';
import {
  selectImpactCalculateVenueCo2ForProject,
  selectImpactCalculateVenueEssForProject,
} from '@/domain/impact-calculate-venue/impact-calculate-venue.selectors';
import { selectImpactEssAnswersMap } from '@/domain/impact-ess/impact-ess.selectors';
import { ProjectVenue } from '@/domain/project-venue/entities/project-venue.entity';
import { selectCurrentProjectVenue } from '@/domain/project-venue/project-venue.selectors';
import { useAppSelector } from '@/ui/hook/store';
import { translate } from '@/ui/i18n';

import { ParityBar } from '../../pdf/components/ParityBar';
import { extendVenueEssCalculate } from '../../pdf/utils/compute-extend-ess';
import {
  displayDecimal,
  formatTopicScore,
} from '../../pdf/utils/display-scores.utils';
import {
  computeTeamParity,
  computeTotalTeamParity,
} from '../../pdf/utils/team-parity.utils';
import ArrowIcon from '../assets/arrow.svg';
import CrossImg from '../assets/cross.svg';
import FairlyScorePillLogo from '../assets/fairly-score-pill-logo.svg';
import { MainScorePill } from '../components/MainScorePill';

export function ScoreCardsVenueC4() {
  document.documentElement.classList.add('score-card');
  document.body.classList.add('score-card');
  dayjs.locale('fr');

  const isAuthenticated = useAppSelector(selectIsAuthenticated);
  const venue = useAppSelector(
    selectCurrentProjectVenue,
  ) satisfies ProjectVenue | null;
  const venueId = venue?.id ?? 0;

  const impactCo2 = useAppSelector(
    selectImpactCalculateVenueCo2ForProject(venueId),
  ) satisfies CalculateVenueCo2;

  const impactEss = extendVenueEssCalculate(
    useAppSelector(selectImpactCalculateVenueEssForProject(venueId)),
  ) satisfies CalculateVenueEss;

  const impactEssScore100 =
    !impactEss.global.score || !impactEss.global.maxScore
      ? 0
      : Math.round((impactEss.global.score * 100) / impactEss.global.maxScore);

  const ESS_ANSWERS = useAppSelector(selectImpactEssAnswersMap);
  const ESS_3 = ESS_ANSWERS?.get('ESS_3')?.value || '';

  const totalArtisticTeamParity = computeTeamParity(impactCo2.dates.byMonth);
  const totalTeamParity = computeTotalTeamParity(
    ESS_3,
    totalArtisticTeamParity,
  );

  if (!isAuthenticated) {
    return <h1>{translate('PdfExports.user-not-authentified')}</h1>;
  }

  if (!venue) {
    return <h1>{translate('PdfExports.venue-not-found')}</h1>;
  }

  return (
    <div id="card4" className="score-card-section" style={{ gap: '4em' }}>
      <div className="title">
        <img
          src={FairlyScorePillLogo}
          width="80"
          height="80"
          alt="Fairly logo"
        />
        <img src={CrossImg} width="30" height="30" />
        <h1>{venue.name}</h1>
      </div>
      <div className="scores">
        <div className="subtitle">
          <div className="score-row-left">
            <img src={ArrowIcon} width="40" height="40" />
            <span>{translate('Projects.ess')}</span>
          </div>
          <MainScorePill
            scoreLetter={impactEss.global.scoreLetter}
            type="ess"
          />
        </div>
        <div className="score-row highlight">
          <span>{translate('PdfExports.total-score')}</span>
          <span className="highlight">
            {displayDecimal(impactEssScore100)} / {100}
          </span>
        </div>
        <div className="score-row highlight">
          <span>{translate('PdfExports.total-score')}</span>
          <span>
            <span className="highlight">
              {displayDecimal(impactEss.global.score)}
            </span>
            /{impactEss.global.maxScore}
          </span>
        </div>
        <div className="score-row">
          <span>{translate('Ess.Topics.PARITY_EQUALITY')}</span>
          <span>
            {formatTopicScore(impactEss.structure.topics.PARITY_EQUALITY)}
          </span>
        </div>
        <div className="score-row">
          <span>{translate('Ess.Topics.ACCESSIBILITY_SOCIAL_DIALOGUE')}</span>
          <span>
            {formatTopicScore(
              impactEss.structure.topics.ACCESSIBILITY_SOCIAL_DIALOGUE,
            )}
          </span>
        </div>
        <div className="score-row">
          <span style={{ width: '60%' }}>
            {translate('Ess.Topics.LOCAL_IMPACT_RSE_ENGAGEMENT')}
          </span>
          <span>
            {formatTopicScore(
              impactEss.structure.topics.LOCAL_IMPACT_RSE_ENGAGEMENT,
            )}
          </span>
        </div>
      </div>

      <div className="scores">
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.8em' }}>
          <Accessibility
            style={{
              width: 50,
              height: 50,
              position: 'relative',
              top: '-0.5em',
            }}
          />
          <div>
            <h3 style={{ margin: '0', lineHeight: '0.7em' }}>
              {' '}
              {translate('ScoreCards.team-parity')}
            </h3>
            <h4
              style={{
                margin: '0 0 0.6em 0',
                fontSize: '0.9em',
                fontWeight: 300,
              }}
            >
              {translate('ScoreCards.team-parity-subtitle')}
            </h4>
          </div>
        </div>
        <ParityBar data={{ parity: totalTeamParity }} />
      </div>

      <div className="edition-date">
        {translate('PdfExports.edition-date', { date: dayjs().format('LL') })}
      </div>
    </div>
  );
}
